import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import Button from '../components/Button';
import CareerCard from '../components/CareerCard';
import SEO from '../components/SEO';
import { getAllCareers, getCategories } from '../services/careerService';

const HomePage = () => {
  const [careers, setCareers] = useState<any[]>([]);
  const categories = getCategories();
  const sliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getAllCareers().then(setCareers);
  }, []);

  const scrollSlider = (direction: 'left' | 'right') => {
    if (sliderRef.current) {
      const scrollAmount = sliderRef.current.clientWidth * 0.8;
      const newScrollLeft = direction === 'left' 
        ? sliderRef.current.scrollLeft - scrollAmount 
        : sliderRef.current.scrollLeft + scrollAmount;
      
      sliderRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="w-full">
      <SEO 
        title="Home"
        description="FuturePath Teens helps teenagers discover their future paths through video-first career content. Explore careers in STEM, Creative, Health, and more."
      />
      
      <section className="relative overflow-hidden bg-brand-bg py-20 lg:py-32 border-b border-brand-border">
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-brand-cyan/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-brand-green/5 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <span className="inline-block px-4 py-1.5 rounded-full bg-brand-dark border border-brand-border text-brand-cyan text-sm font-medium mb-6">
            DISCOVER CAREERS
          </span>
          <h1 className="text-5xl md:text-7xl font-heading font-bold text-brand-white mb-6 tracking-tight leading-tight">
            FuturePath <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-cyan to-brand-green">Teens</span>
          </h1>
          <p className="max-w-2xl mx-auto text-lg md:text-xl text-brand-grey2 mb-10 leading-relaxed">
            Discover your future career — one simple guide at a time.
            Explore real-world careers explained in a way teens can understand.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/careers">
              <Button size="lg" className="w-full sm:w-auto shadow-lg shadow-brand-cyan/20">Start Exploring</Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-brand-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-8">
            <div>
              <h2 className="text-3xl font-heading font-semibold text-brand-white mb-2">Explore Future Paths</h2>
              <p className="text-brand-grey2">Featured and trending careers for you.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => scrollSlider('left')} className="p-2 rounded-full border border-brand-border text-brand-grey1 hover:text-brand-cyan hover:border-brand-cyan transition-colors">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button onClick={() => scrollSlider('right')} className="p-2 rounded-full border border-brand-border text-brand-grey1 hover:text-brand-cyan hover:border-brand-cyan transition-colors">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div ref={sliderRef} className="flex gap-6 overflow-x-auto pb-8 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
            {careers.map((career) => (
              <div key={career.slug} className="min-w-[300px] md:min-w-[350px] snap-start">
                <CareerCard career={career} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-brand-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-heading font-semibold text-brand-white mb-12 text-center">Browse by Category</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((cat) => (
              <Link key={cat.id} to={`/categories/${cat.id}`} className="group p-8 rounded-2xl bg-brand-light border border-brand-border hover:border-brand-cyan transition-all duration-300 hover:-translate-y-1">
                <h3 className="text-xl font-heading font-semibold text-brand-white mb-2 group-hover:text-brand-cyan transition-colors">{cat.label}</h3>
                <p className="text-brand-grey2 text-sm mb-4">{cat.description}</p>
                <div className="flex items-center text-brand-cyan text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity -translate-x-2 group-hover:translate-x-0">
                  View Careers <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;